<template>
  <v-row>
    <v-col cols="12" sm="12">
      <v-card>
        <v-card-title>{{ $t("Tiedostot") }}</v-card-title>
        <v-divider></v-divider>

        <!-- Breadcrumb Navigation -->
        <v-card-text v-if="breadcrumbTrail.length > 0" class="py-2">
          <div class="d-flex align-center flex-wrap">
            <v-icon size="20" class="mr-2">{{ icons.mdiFolderOutline }}</v-icon>
            <span class="text-body-2 cursor-pointer primary--text" @click="showAllResources">
              {{ $t("Kaikki tiedostot") }}
            </span>
            <template v-for="(folder, index) in breadcrumbTrail">
              <span :key="`breadcrumb-${index}`">
                <v-icon size="16" class="mx-2">{{ icons.mdiChevronRight }}</v-icon>
                <span class="text-body-2"
                  :class="index === breadcrumbTrail.length - 1 ? 'font-weight-medium' : 'cursor-pointer primary--text'"
                  @click="index !== breadcrumbTrail.length - 1 ? navigateToBreadcrumb(index) : null">
                  {{ folder.title }}
                </span>
              </span>
            </template>
          </div>
        </v-card-text>

        <v-list three-line>
          <template v-for="(item, index) in combinedList">
            <v-list-item :target="item.itemType === 'resource' ? '_blank' : null" :key="item.id"
              :href="item.itemType === 'resource' && (item.require_acknowledgment == false || tenant.settings.acknowledgments_enabled == false) ? getItemLink(item) : null"
              @click="item.itemType === 'folder' ? openFolder(item) : view(item)">
              <v-list-item-avatar tile>
                <v-icon v-if="item.itemType === 'folder'" size="32" color="primary">
                  {{ icons.mdiFolderOutline }}
                </v-icon>
                <v-icon v-else-if="item.resource_type == 1" size="32">
                  {{ icons.mdiAttachment }}
                </v-icon>
                <v-icon v-else size="32">{{ icons.mdiLink }}</v-icon>
              </v-list-item-avatar>

              <v-list-item-content>
                <v-list-item-title class="text-lg mb-1">
                  {{ item.title }}
                </v-list-item-title>

                <v-list-item-subtitle>
                  <v-chip small>
                    {{
                      item.itemType === 'folder'
                        ? $t("Kansio")
                        : item.resource_type == 1
                          ? $t("Tiedosto")
                          : $t("Linkki")
                    }}
                  </v-chip>
                  {{ item.description }}
                </v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action v-if="item.itemType === 'resource'">
                <v-list-item-action-text>
                  <v-icon v-if="item.require_acknowledgment && tenant.settings.acknowledgments_enabled" class="mr-2"
                    :title="$t('Lukukuittaus vaaditaan')" :color="item.read ? 'success' : 'error'">
                    {{ icons.mdiDraw }}
                  </v-icon>
                  <v-chip small>
                    {{ item.group_name }}
                  </v-chip>
                </v-list-item-action-text>
              </v-list-item-action>
            </v-list-item>
            <v-divider v-if="index < combinedList.length - 1"></v-divider>
          </template>
        </v-list>
        <div class="d-flex align-end flex-column mt-3" v-if="pages > 1">
          <v-pagination v-model="page" :length="pages" :total-visible="8"></v-pagination>
        </div>
      </v-card>
    </v-col>
    <v-dialog v-model="isDownloading" hide-overlay persistent width="240">
      <v-card color="primary" dark>
        <v-card-text class="pt-2">
          {{ $t("Ladataan...") }}

          <v-progress-linear indeterminate color="white" class="mb-0 mt-2"></v-progress-linear>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Acknowledgment Dialog -->
    <v-dialog v-model="acknowledgmentDialog" max-width="600" v-if="selectedResource">
      <v-card>
        <v-card-title class="pb-2">
          <v-icon class="mr-2">{{ icons.mdiDraw }}</v-icon>
          {{ $t('Lukukuittaus') }}
        </v-card-title>

        <v-card-text class="pt-3">
          <!-- Status alerts -->
          <v-alert v-if="acknowledgmentStatus === 'current'" type="success" text class="mb-3">
            {{ $t('Olet kuitannut tämän luetuksi') }} {{ acknowledgmentDate ? acknowledgmentDate : '' }}
          </v-alert>

          <v-alert v-else-if="acknowledgmentStatus === 'outdated'" type="warning" text class="mb-3">
            {{ $t('Sisältö on päivittynyt viime kuittauksesi jälkeen. Kuittaa uudelleen.') }}
          </v-alert>

          <v-alert v-else-if="acknowledgmentStatus === 'none'" type="info" text class="mb-3">
            {{ $t('Tämä sisältö vaatii lukukuittauksen.') }}
          </v-alert>

          <!-- Resource access button -->
          <v-btn color="primary" outlined @click="accessResource(selectedResource)" class="mb-3" block>
            <v-icon left>
              {{ selectedResource.resource_type == 1 ? icons.mdiAttachment : icons.mdiLink }}
            </v-icon>
            {{ selectedResource.resource_type == 1 ? $t('Avaa tiedosto') : $t('Avaa linkki') }}
          </v-btn>

          <!-- Acknowledgment description if available -->
          <div v-if="selectedResource.acknowledgment_description && acknowledgmentStatus !== 'current'" class="mb-3">
            <div>{{ selectedResource.acknowledgment_description }}</div>
          </div>

          <!-- Acknowledgment button -->
          <v-btn v-if="acknowledgmentStatus !== 'current'" color="primary" :loading="isSubmittingAcknowledgment"
            @click="submitAcknowledgment" block>
            {{ $t('Kuittaa luetuksi') }}
          </v-btn>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="acknowledgmentDialog = false">
            {{ $t('Sulje') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-row>
</template>

<script>
import { computed, onMounted, ref, watch } from "@vue/composition-api";
import api from "@/api/";
import { mdiLink, mdiAttachment, mdiFolderOutline, mdiChevronRight, mdiDraw } from "@mdi/js";
import { useTranslations } from "@core/libs/i18n";
import resource_types from "@core/utils/resource_types";
import AcknowledgmentTypes from "@/@core/utils/acknowledgment_types";
import download from "downloadjs";
import { getDateTime } from "@/@core/utils/datetime_helpers";
import useAuth from "@/useAuth";

export default {
  setup() {
    const resources = ref([]);
    const folders = ref([]);
    const totalCount = ref(0);
    const breadcrumbTrail = ref([]);
    const page = ref(1);
    const pageSize = 10;
    const isDownloading = ref(false);
    const { tenant } = useAuth();
    const { t } = useTranslations();

    // Acknowledgment state
    const acknowledgmentDialog = ref(false);
    const selectedResource = ref(null);
    const acknowledgmentStatus = ref(null); // null, 'current', 'outdated', 'none'
    const acknowledgmentDate = ref(null);
    const isSubmittingAcknowledgment = ref(false);

    // Computed property for current folder ID (last item in breadcrumb trail)
    const currentFolderId = computed(() => {
      return breadcrumbTrail.value.length > 0
        ? breadcrumbTrail.value[breadcrumbTrail.value.length - 1].id
        : null;
    });

    // fetch data
    const fetchResources = async () => {
      const response = await api.getResources(page.value, pageSize, currentFolderId.value);

      folders.value = response.data.folders || [];
      resources.value = response.data.resources.results;
      totalCount.value = response.data.resources.count;
    };

    watch(page, () => {
      fetchResources();
    });

    onMounted(() => {
      fetchResources();
    });

    const view = async (resource) => {
      if (resource.require_acknowledgment && tenant.value.settings.acknowledgments_enabled) {
        selectedResource.value = resource;
        await checkAcknowledgmentStatus(resource);
        acknowledgmentDialog.value = true;
      }
      else if (resource.resource_type == resource_types.document.value) {
        isDownloading.value = true;

        const response = await api.downloadResource(resource.id);
        if (response.ok) {
          download(response.data, resource.filename);
        } else {
          alert(response.error);
        }
        isDownloading.value = false;
      }
    };
    const getItemLink = (resource) => {
      if (resource.resource_type == resource_types.document.value) return null;

      return resource.link;
    };

    const pages = computed(() => {
      return Math.ceil(totalCount.value / pageSize);
    });

    const combinedList = computed(() => {
      const resourceItems = resources.value.map(resource => ({
        ...resource,
        itemType: 'resource'
      }));

      if (page.value === 1) {
        const folderItems = folders.value.map(folder => ({
          ...folder,
          itemType: 'folder'
        }));

        return [...folderItems, ...resourceItems];
      } else {
        return resourceItems;
      }
    });

    // Open a folder
    const openFolder = (folder) => {
      breadcrumbTrail.value.push({ id: folder.id, title: folder.title });
      page.value = 1;
      fetchResources();
    };

    // Navigate to a specific breadcrumb level
    const navigateToBreadcrumb = (index) => {
      breadcrumbTrail.value = breadcrumbTrail.value.slice(0, index + 1);
      page.value = 1;
      fetchResources();
    };

    // Show all resources (clear folder filter)
    const showAllResources = () => {
      breadcrumbTrail.value = [];
      page.value = 1;
      fetchResources();
    };

    // Acknowledgment methods
    const checkAcknowledgmentStatus = async (resource) => {
      try {
        const response = await api.getUserAcknowledgments(AcknowledgmentTypes.Resource, resource.id);

        if (response.ok) {
          const acknowledgments = response.data;

          if (!acknowledgments || acknowledgments.length === 0) {
            acknowledgmentStatus.value = 'none';
            acknowledgmentDate.value = null;
            return;
          }

          const currentVersion = resource.acknowledgment_version;
          const latestAcknowledgment = acknowledgments.find(a => a.version == currentVersion);

          if (latestAcknowledgment) {
            acknowledgmentStatus.value = 'current';
            acknowledgmentDate.value = getDateTime(new Date(latestAcknowledgment.acknowledged_at));
          } else {
            acknowledgmentStatus.value = 'outdated';
            acknowledgmentDate.value = null;
          }
        }
      } catch (error) {
        console.error('Error checking acknowledgment status:', error);
        acknowledgmentStatus.value = 'none';
        acknowledgmentDate.value = null;
      }
    };

    const submitAcknowledgment = async () => {
      if (!selectedResource.value || !selectedResource.value.id) return;

      isSubmittingAcknowledgment.value = true;

      try {
        const currentVersion = selectedResource.value.acknowledgment_version || 1;
        const response = await api.submitAcknowledgment(
          AcknowledgmentTypes.Resource,
          selectedResource.value.id,
          currentVersion
        );

        if (response.ok) {
          acknowledgmentStatus.value = 'current';
          acknowledgmentDate.value = getDateTime(new Date());
        }
      } catch (error) {
        console.error('Error submitting acknowledgment:', error);
        alert('Failed to submit acknowledgment. Please try again.');
      } finally {
        isSubmittingAcknowledgment.value = false;
      }
    };

    const accessResource = async (resource) => {
      if (resource.resource_type == resource_types.document.value) {
        isDownloading.value = true;

        const response = await api.downloadResource(resource.id);
        if (response.ok) {
          download(response.data, resource.filename);
        } else {
          alert(response.error);
        }
        isDownloading.value = false;
      } else {
        // For links, open in new tab
        window.open(resource.link, '_blank');
      }
    };

    return {
      resources,
      folders,
      combinedList,
      totalCount,
      breadcrumbTrail,
      currentFolderId,
      getItemLink,
      view,
      openFolder,
      navigateToBreadcrumb,
      showAllResources,
      page,
      pages,
      tenant,
      isDownloading,
      // Acknowledgment
      acknowledgmentDialog,
      selectedResource,
      acknowledgmentStatus,
      acknowledgmentDate,
      isSubmittingAcknowledgment,
      checkAcknowledgmentStatus,
      submitAcknowledgment,
      accessResource,
      icons: {
        mdiLink,
        mdiAttachment,
        mdiFolderOutline,
        mdiChevronRight,
        mdiDraw,
      },
      t,
    };
  },
};
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}
</style>
